import React, { useContext, useEffect, useState } from 'react'
import { Button, Flex, IconButton, Stack } from '@airbus/components-react/components'
import { useTranslation } from 'react-i18next'
import ModalPn from 'components/Modal/modalPn'
import { FormProvider, useForm } from 'react-hook-form'
import FormInput from 'components/FormFields/FormInput'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons'
import FormSelect from 'components/FormFields/FormSelect'

import urlApis from 'utils/api/urlApis'
import useQuery from 'utils/hooks/useQuery'
import { PnSearch } from 'types/pn'
import { Company, Store } from 'types/stock'
import { renderOptionLabel } from 'pages/WorkOrder/components/WoDetails/woDetails'
import LangContext from 'context/LangContext'
import AuthContext from 'context/AuthContext'
import { StockSearchInputs } from '../StockPage'
import { useSearchParams } from 'react-router-dom'
import FormControl from 'components/FormFields/FormControl'

const DEFAULT_OPTION = { label: '-', value: '' }

const StockForm = ({
  onSubmit,
  pn,
  company,
  store,
}: {
  onSubmit: (d: StockSearchInputs) => void
  pn: string
  company: string
  store: string
}) => {
  const { t } = useTranslation()
  const [modalPnOpened, setModalPnOpened] = useState(false)
  const [companyOptions, setCompanyOptions] = useState([DEFAULT_OPTION])
  const [storeOptions, setStoreOptions] = useState([DEFAULT_OPTION])
  const formTitle = t('7001180')
  const { lang } = useContext(LangContext)
  const [searchParams, setSearchParams] = useSearchParams()

  const { fetchData: fetchCompanyOptions } = useQuery<Company[]>(urlApis.URL_COMMON_LOCATION_LIST_COMPANIES_GET)
  const { fetchData: fetchStoreOptions } = useQuery<Store[]>(urlApis.URL_COMMON_LOCATION_LIST_STORES)

  const methods = useForm<StockSearchInputs>({
    defaultValues: {
      pn,
      company,
      store,
    },
  })

  const {
    formState: { errors, isSubmitting, isDirty, defaultValues },
    handleSubmit,
    setValue,
  } = methods

  const handlePnSelected = (equipment: PnSearch) => {
    setModalPnOpened(false)
    methods.setValue('pn', equipment?.pn, { shouldDirty: true })
    setSearchParams({ pn: equipment.pn })
  }

  const updateStoreOptions = async (selectedCompany: string) => {
    setValue('store', '')
    if (selectedCompany) {
      loadStoreOptions(selectedCompany)
    } else {
      setStoreOptions([DEFAULT_OPTION])
    }
  }

  const loadCompanyOptions = async () => {
    const responseCompanyOptions = await fetchCompanyOptions()
    const compOpts =
      responseCompanyOptions?.data
        ?.sort((a, b) => (a.code > b.code ? 1 : b.code > a.code ? -1 : 0))
        ?.map((comp) => ({ ...comp, value: comp.code, label: comp.name })) || []

    setCompanyOptions([DEFAULT_OPTION, ...compOpts])
  }

  const loadStoreOptions = async (selectedCompany = '') => {
    const response = await fetchStoreOptions({ company: selectedCompany })

    const options =
      response?.data
        ?.sort((a, b) => (a.storeCode > b.storeCode ? 1 : b.storeCode > a.storeCode ? -1 : 0))
        ?.map((store) => ({
          value: store.storeCode,
          label: store.storeName,
        })) || []

    setStoreOptions([DEFAULT_OPTION, ...options])
  }

  useEffect(() => {
    loadCompanyOptions()
    loadStoreOptions(defaultValues?.company)
  }, [])

  return (
    <>
      <FormProvider {...methods}>
        <form style={{ marginTop: '1em' }} onSubmit={handleSubmit(onSubmit)}>
          <Flex spacing='2-x' style={{ width: '100%' }} childrenStyle={{ flex: 1, alignSelf: 'end' }}>
            <FormInput
              name='pn'
              label={
                <>
                  {t('9500047')}
                  <IconButton
                    variant='ghost'
                    size='xsmall'
                    style={{ marginLeft: '1ex' }}
                    onClick={() => setModalPnOpened(true)}
                  >
                    <FontAwesomeIcon icon={faExternalLinkAlt} />
                  </IconButton>
                </>
              }
              onChange={(p: string) => setSearchParams({ company, store, pn: p })}
              disabled={isSubmitting}
              maxLength={40}
              capitalize
              error={Boolean(errors.pn)}
              rules={{ required: true }}
            />

            <FormControl label={t('7000876')} required>
              <Flex spacing='1-x' childrenStyle={{ flex: 1 }}>
                <FormSelect
                  name='company'
                  rules={{ required: true }}
                  disabled={isSubmitting}
                  options={companyOptions}
                  error={Boolean(errors?.company)}
                  onChange={(comp) => {
                    updateStoreOptions(comp)
                    setSearchParams({ pn, store, company: comp })
                  }}
                  getOptionLabel={renderOptionLabel}
                  capitalize
                  compact
                />
                <FormSelect
                  name='store'
                  hideAsterisk
                  rules={{ required: true }}
                  disabled={isSubmitting}
                  options={storeOptions}
                  error={Boolean(errors?.store)}
                  getOptionLabel={renderOptionLabel}
                  onChange={(stor: string) => setSearchParams({ pn, company, store: stor })}
                  capitalize
                  compact
                />
              </Flex>
            </FormControl>

            <Stack style={{ justifyContent: 'end' }}>
              <Button variant='secondary' type='submit' disabled={!isDirty || isSubmitting}>
                {t('7001655')}
              </Button>
            </Stack>
          </Flex>
        </form>
      </FormProvider>

      <ModalPn
        formTitle={formTitle}
        lang={lang}
        isOpened={modalPnOpened}
        onCloseModal={() => setModalPnOpened(false)}
        onPnSelected={handlePnSelected}
        noPnFilter
      />
    </>
  )
}

export default StockForm
